import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { Helmet } from "react-helmet-async";
import { ChevronRight, ArrowRight, Home } from "lucide-react";
import { useT } from "@/hooks/useT";

const SITE_URL = "https://akomoda.co";

function buildBreadcrumbSchema(breadcrumb) {
  if (!breadcrumb || breadcrumb.length === 0) return null;
  const items = [
    { "@type": "ListItem", position: 1, name: "Kreu", item: SITE_URL + "/" },
    ...breadcrumb.map((b, i) => ({
      "@type": "ListItem",
      position: i + 2,
      name: b.label,
      ...(b.to ? { item: SITE_URL + b.to } : {}),
    })),
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export default function PageHeader({ eyebrow, title, description, ctas = [], breadcrumb = [] }) {
  const t = useT();
  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumb);

  return (
    <section className="relative isolate overflow-hidden bg-surface-1" data-testid="page-header">
      {breadcrumbSchema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        </Helmet>
      )}
      {/* Ambient warm glow */}
      <div className="pointer-events-none absolute -top-40 right-[-12%] h-[480px] w-[640px] bg-[radial-gradient(closest-side,rgba(255,122,0,0.10),transparent)] blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -top-32 left-[-10%] h-[360px] w-[460px] bg-[radial-gradient(closest-side,rgba(15,23,42,0.05),transparent)] blur-3xl" aria-hidden />
      <div className="absolute inset-0 bg-dot opacity-30 mask-fade-y" aria-hidden />

      <div className="container-akm relative pt-14 pb-20 md:pt-20 md:pb-28">
        {breadcrumb.length > 0 && (
          <nav className="mb-7 flex items-center gap-1.5 text-[12px] text-slate3" aria-label="Breadcrumb">
            <Link to="/" className="inline-flex items-center gap-1 transition-colors hover:text-[#FF7A00]">
              <Home size={11} />
              {t("Kreu", "Home")}
            </Link>
            {breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <ChevronRight size={12} className="text-line2" />
                {b.to ? (
                  <Link to={b.to} className="transition-colors hover:text-[#FF7A00]">{b.label}</Link>
                ) : (
                  <span className="font-medium text-ink">{b.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        <div className="max-w-3xl">
          <h1 className="h1-akm" data-testid="page-title">{title}</h1>
          {description && (
            <p className="lead mt-6 max-w-2xl text-[17px]">{description}</p>
          )}
          {ctas.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-3">
              {ctas.map((c, i) => (
                <Link
                  key={i}
                  to={c.to}
                  className={c.variant === "ghost" ? "btn-ghost !px-6 !py-3" : "btn-primary !px-6 !py-3"}
                  data-testid={`page-cta-${i}`}
                >
                  {c.label} {c.variant !== "ghost" && <ArrowRight size={14} />}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom subtle hairline */}
      <div className="absolute inset-x-0 bottom-0 section-divider" aria-hidden />
    </section>
  );
}
